import { useEffect, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import SORT_OPTIONS from '@/shared/constants/finishedGamesSortOptions';
import { queryKeys } from '@/shared/constants/queryKeys';
import routesPath from '@/shared/constants/routesPath';

import useSearchParamsManager from '@/hooks/useSearchParamsManager';
import { getFinishedGames, searchFinishedGames } from '@/services/livescoring';

const SORT_LABELS = {
  [SORT_OPTIONS.DATE]: 'Date',
  [SORT_OPTIONS.PLAYER]: 'Player name',
  [SORT_OPTIONS.TOTAL]: 'Total',
};

export default function useFinishedGames() {
  const { query, orderCriteria, updateSearchParams } = useSearchParamsManager({
    defaultOrderCriteria: {
      order: SORT_OPTIONS.DATE,
      sort: 'asc',
    },
  });
  const params = useParams();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState(query);
  // update search params but debounce
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchTerm.length > 3) {
        updateSearchParams({ q: searchTerm });
      } else if (searchTerm.length <= 3) {
        updateSearchParams({
          q: '',
          ...orderCriteria,
        });
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // useQuery hook
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    isFetching,
  } = useInfiniteQuery({
    queryKey: [
      queryKeys.finishedGames,
      query,
      orderCriteria.order,
      orderCriteria.sort,
      params.uuid,
    ],
    queryFn: ({ pageParam = 1 }) => {
      if (query !== '') {
        return searchFinishedGames({
          uuid: params.uuid,
          page: pageParam,
          query,
        });
      }
      return getFinishedGames({
        uuid: params.uuid,
        page: pageParam,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.data.length === 50 ? allPages.length + 1 : undefined;
    },
    select: formatData,
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const goToLiveScores = () => {
    navigate(generatePath(routesPath.LIVESCORING, { uuid: params.uuid }));
  };

  return {
    data: {
      games: data || [],
      isPending,
      isFetching,
      hasError: !!error,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    },
    sort: {
      labels: SORT_LABELS,
      selected: orderCriteria,
      onChange: updateSearchParams,
    },
    searchTerm,
    onSearch: handleSearch,
    goToLiveScores,
  };
}

const formatData = ({ pages }) => {
  const blocks = pages
    .map((page) =>
      page.data.map((game) => ({
        ...game,
        date: new Date(game.modified * 1000),
      }))
    )
    .flat();

  if (blocks[0]?.games) {
    const processedBlocks = blocks.map((block) => ({
      ...block,
      gamesIds: block.games.map((game) => game.id),
      gameScores: block.games.map((game) => game.score),
    }));
    return processedBlocks;
  }
  return blocks;
};
